import { ArrowDown, ArrowUp, FileText, PlayCircle } from "lucide-react";

const MAX = 20;
const Chip = ({ on, onClick, children, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick}
    className={`px-4 h-10 rounded-md text-sm border transition-colors ${on ? "bg-indigo-500 border-indigo-500 text-white" : "border-[#242C3D] text-slate-400 hover:text-white"}`}>{children}</button>
);
const Icon = ({ p }) => (p.campaign_type === "whitepaper" ? <FileText className="w-4 h-4 text-indigo-300" /> : <PlayCircle className="w-4 h-4 text-pink-300" />);
const Tag = ({ p }) => <span className="text-[10px] font-mono uppercase text-slate-500 shrink-0">{p.hub_only ? "hub upload" : "landing page"}</span>;

export const HubLayoutEditor = ({ hub, pages, onChange, isNew }) => {
  const set = (p) => onChange({ ...hub, ...p });
  const ids = new Set(pages.map((p) => p.id));
  const byAge = [...pages].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
  const order = (hub.order?.length ? hub.order : byAge.filter((p) => p.status === "active").map((p) => p.id)).filter((x) => ids.has(x)).slice(0, MAX);
  const byId = Object.fromEntries(pages.map((p) => [p.id, p]));
  const toggle = (id) => {
    const next = order.includes(id) ? order.filter((x) => x !== id) : [...order, id];
    set({ order: next, primary_id: next.includes(hub.primary_id) ? hub.primary_id : "" });
  };
  const move = (i, d) => { const s = [...order]; [s[i], s[i + d]] = [s[i + d], s[i]]; set({ order: s }); };
  const reader = hub.layout === "reader";

  return (
    <section className="rounded-lg border border-[#242C3D] bg-[#141A24]/70 p-6 space-y-5" data-testid="hub-layout-editor">
      <div>
        <h2 className="font-display font-semibold text-lg">Content hub layout</h2>
        <p className="text-xs text-slate-500 mt-1">Choose up to {MAX} assets (landing pages and hub uploads) and their order. Card grid shows them as cards; reading layout shows the featured one in the main pane and the rest in a left pane.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Chip on={!reader} onClick={() => set({ layout: "grid" })} testid="hub-layout-grid">Card grid</Chip>
        <Chip on={reader} onClick={() => !isNew && pages.length && set({ layout: "reader", order, primary_id: hub.primary_id || order[0] || "" })} testid="hub-layout-reader">Reading layout (featured + left pane)</Chip>
      </div>
      {(isNew || !pages.length) ? (
        <p className="text-sm text-amber-300" data-testid="hub-layout-no-pages">{isNew ? "Save this campaign first, then add landing pages or upload content to the hub." : "Add landing pages or upload content to the hub first."}</p>
      ) : (
        <div className="space-y-5">
          {reader && (
            <div>
              <label className="block font-mono text-[11px] uppercase tracking-widest text-slate-500 mb-2">Featured asset (main reading pane)</label>
              <select data-testid="hub-primary-select" value={hub.primary_id} className="w-full h-11 px-4 rounded-md bg-[#0B0F17] border border-[#242C3D] text-sm"
                onChange={(e) => set({ primary_id: e.target.value, order: [e.target.value, ...order.filter((x) => x !== e.target.value)] })}>
                <option value="">Choose…</option>
                {order.map((id) => <option key={id} value={id}>{`${byId[id].title} — ${byId[id].campaign_type === "whitepaper" ? "White paper" : "Video"}`}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="block font-mono text-[11px] uppercase tracking-widest text-slate-500 mb-2">Shown in the hub ({order.length}/{MAX})</label>
            <ol className="space-y-1.5" data-testid="hub-order-list">
              {order.map((id, i) => (
                <li key={id} data-testid={`hub-order-item-${id}`} className="flex items-center gap-3 text-sm rounded-md bg-[#0B0F17] border border-indigo-500/40 px-3 h-11">
                  <input type="checkbox" checked disabled={order.length === 1} title={order.length === 1 ? "A hub needs at least one asset" : undefined} data-testid={`hub-include-${id}`} onChange={() => toggle(id)} className="accent-indigo-500" />
                  <span className="font-mono text-xs text-slate-500 w-5">{i + 1}</span>
                  <Icon p={byId[id]} /><span className="truncate flex-1">{byId[id].title}</span>
                  {reader && id === hub.primary_id && <span className="text-[10px] font-mono uppercase text-amber-300">featured</span>}
                  {byId[id].status !== "active" && <span className="text-[10px] font-mono uppercase text-amber-300">draft — hidden</span>}
                  <Tag p={byId[id]} />
                  <button type="button" data-testid={`hub-order-up-${i}`} disabled={i === 0} onClick={() => move(i, -1)} className="text-slate-400 hover:text-white disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
                  <button type="button" data-testid={`hub-order-down-${i}`} disabled={i === order.length - 1} onClick={() => move(i, 1)} className="text-slate-400 hover:text-white disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
                </li>
              ))}
            </ol>
          </div>
          {pages.some((p) => !order.includes(p.id)) && (
            <div>
              <label className="block font-mono text-[11px] uppercase tracking-widest text-slate-500 mb-2">Not shown</label>
              <div className="space-y-1.5">
                {pages.filter((p) => !order.includes(p.id)).map((p) => (
                  <label key={p.id} className={`flex items-center gap-3 rounded-md border border-[#242C3D] px-3 h-11 text-sm ${order.length >= MAX ? "opacity-50" : "cursor-pointer"}`}>
                    <input type="checkbox" checked={false} disabled={order.length >= MAX} data-testid={`hub-include-${p.id}`} onChange={() => toggle(p.id)} className="accent-indigo-500" />
                    <Icon p={p} /><span className="truncate flex-1">{p.title}</span><Tag p={p} />
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
};
